import React, {
  createContext, useState, useContext, useEffect,
  useMemo,
} from 'react'
import { useAsync } from 'react-async'

import { captureException } from '@sentry/react'
import {
  fetchCategories,
} from './services'
import { DeviceCtx } from './DeviceCtx'
import { useLocalStorageState } from './hooks'
import { extraBadges } from './constants/extraBadges'
import { getCustomer } from './helpers/utils'
import { getPhoneFromToken } from './helpers/decodeJWT'
import { fetchSession, createSession, updateSession } from './services/sessions'

export const BadgesCtx = createContext({
  currentCategory: undefined,
  selectBadge: undefined,
  setCurrentTaskId: () => {},
  currentTask: {},
  showOnboarding: false,
  setShowOnboarding: () => {},
})

export const BadgesCtxProvider = ({ children }) => {
  const { referenceId } = useContext(DeviceCtx)
  const customer = `wc-${getCustomer()}`

  const {
    data: categories = [],
    error,
    reload: reloadFetchCategories,
  } = useAsync(fetchCategories, {
    referenceId,
  })
  const [currentCategoryId, setCurrentCategoryId] = useLocalStorageState('currentCategoryId')
  const [currentTaskId, setCurrentTaskId] = useLocalStorageState('currentTaskId')
  const [completedTutorialsStorage, setCompletedTutorialsStorage] = useState({})
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [sessionId, setSessionId] = useState()

  const phoneNumberFromToken = getPhoneFromToken()

  const loadProgress = async () => {
    if (!referenceId) return

    let initialData
    const emptyState = {
      [customer]: {
        [referenceId]: [],
        extraBadges: {},
      },
    }

    const legacyCompletedTutorial = localStorage.getItem('completedTutorial')
    if (legacyCompletedTutorial) {
      initialData = {
        ...emptyState,
        ...JSON.parse(legacyCompletedTutorial),
      }
      localStorage.removeItem('completedTutorial')
    } else {
      initialData = emptyState
    }

    try {
      const { completedTutorials: sessionData, sessionId: id } = await fetchSession({ initialData })
      setSessionId(id)
      const isWelcomeBadgeDone = sessionData?.[customer]?.extraBadges?.['Welcome to the family']?.isDone
      setShowOnboarding(!isWelcomeBadgeDone)
      if (sessionData) {
        if (!sessionData[customer]) updateSession({ ...initialData, ...sessionData })
        setCompletedTutorialsStorage({ ...initialData, ...sessionData })
      } else {
        setCompletedTutorialsStorage(initialData)
      }
    } catch (err) {
      setCompletedTutorialsStorage(initialData)
      captureException(err)
      setShowOnboarding(false)
    }
  }

  const saveProgress = async (updatedData) => {
    if (!phoneNumberFromToken) return

    try {
      await updateSession({ completedTutorials: updatedData, sessionId })
    } catch (err) {
      captureException(err)
    }
  }

  const getCompletedTutorialIds = () => completedTutorialsStorage[customer]?.[referenceId] || []

  const addCompletedTutorialId = async (tutorialId) => {
    const currentCompletedIds = getCompletedTutorialIds()

    if (currentCompletedIds.includes(tutorialId)) return

    const updatedStorage = {
      ...completedTutorialsStorage,
      [customer]: {
        ...completedTutorialsStorage[customer],
        [referenceId]: [...currentCompletedIds, tutorialId],
      },
    }

    setCompletedTutorialsStorage(updatedStorage)
    await saveProgress(updatedStorage)
  }

  const getCategoryWithProgress = (categoryData) => {
    const completedIds = getCompletedTutorialIds()
    const tutorialsWithStatus = categoryData.wcTutorials?.map((tutorial) => ({
      ...tutorial,
      isDone: completedIds.includes(tutorial.id),
    })) || []

    const completedCount = tutorialsWithStatus.filter(
      (tutorial) => tutorial.isDone,
    ).length
    const progressPercentage = tutorialsWithStatus.length > 0
      ? Math.round((completedCount / tutorialsWithStatus.length) * 100)
      : 0

    return {
      ...categoryData,
      wcTutorials: tutorialsWithStatus,
      isDone: progressPercentage === 100,
      progress: progressPercentage,
    }
  }

  const getCategoryById = (categoryId) => {
    const categoryData = categories.find(
      (category) => category.id === categoryId,
    )
    return categoryData ? getCategoryWithProgress(categoryData) : {}
  }

  const currentCategory = getCategoryById(currentCategoryId)

  const getTask = (taskId) => {
    const tasks = currentCategory?.wcTutorials || []
    return tasks.find((task) => task.id === taskId) || {}
  }

  const updateProgress = () => {
    const currentTask = getTask(currentTaskId)
    if (currentTask.id) {
      addCompletedTutorialId(currentTask.id)
    }
  }

  const completeExtraBadge = async (name) => {
    const completedBadges = completedTutorialsStorage[customer]?.extraBadges || {}

    if (completedBadges[name]?.isDone) return

    const updatedStorage = {
      ...completedTutorialsStorage,
      [customer]: {
        ...completedTutorialsStorage[customer],
        extraBadges: {
          ...completedBadges,
          [name]: {
            isDone: true,
            tasks: completedBadges[name]?.tasks || undefined,
          },
        },
      },
    }

    setCompletedTutorialsStorage(updatedStorage)
    await saveProgress(updatedStorage)
  }

  const completeExtraBadgeTask = async ({ taskTitle, badgeName }) => {
    const completedBadges = completedTutorialsStorage[customer]?.extraBadges || {}

    if (completedBadges[badgeName]?.tasks.includes(taskTitle)) return

    const updatedStorage = {
      ...completedTutorialsStorage,
      [customer]: {
        ...completedTutorialsStorage[customer],
        extraBadges: {
          ...completedBadges,
          [badgeName]: {
            isDone: false,
            tasks: [taskTitle, ...completedBadges[badgeName]?.tasks || []],
          },
        },
      },
    }
    setCompletedTutorialsStorage(updatedStorage)
    await saveProgress(updatedStorage)
  }

  const migrateLegacyProgress = () => {
    const oldProgress = JSON.parse(localStorage.getItem('progress'))
    if (!oldProgress) return

    const newStructure = {}

    oldProgress.forEach((item) => {
      if (item.wcTutorials) {
        item.wcTutorials.forEach((wcTutorial) => {
          if (wcTutorial.isDone && wcTutorial.tutorial?.product?.references) {
            wcTutorial.tutorial.product.references.forEach((ref) => {
              const refId = ref.reference_id
              if (!newStructure[refId]) newStructure[refId] = []
              if (!newStructure[refId].includes(wcTutorial.id)) {
                newStructure[refId].push(wcTutorial.id)
              }
            })
          }
        })
      }

      if (item.isExtraBadges) {
        if (!newStructure.extraBadges) newStructure.extraBadges = {}
        newStructure.extraBadges[item.name] = {
          isDone: item.isDone,
          ...(item.tasks && {
            tasks: item.tasks.filter((task) => task.isDone).map((task) => task.title),
          }),
        }
      }
    })

    localStorage.setItem('completedTutorial', JSON.stringify({ [customer]: newStructure }))
    localStorage.removeItem('progress')
    localStorage.removeItem('generalProgress')
    localStorage.removeItem('extraBadges')
  }

  const currentTask = getTask(currentTaskId)

  const categoriesWithProgress = useMemo(() => categories.map(getCategoryWithProgress),
    [categories, completedTutorialsStorage[customer]])

  const extraBadgesProgress = useMemo(() => {
    const completedExtraBadges = completedTutorialsStorage[customer]?.extraBadges
    return completedExtraBadges ? extraBadges.map((badge) => ({
      ...badge,
      isDone: completedExtraBadges[badge.name]?.isDone,
      tasks: badge.tasks ? badge.tasks.map((task) => ({
        ...task,
        isDone: completedExtraBadges[badge.name]?.tasks.includes(task.title),
      })) : undefined,
    })) : extraBadges
  }, [completedTutorialsStorage[customer]?.extraBadges])

  const allBadgesWithExtra = [
    ...categoriesWithProgress,
    ...(extraBadgesProgress || []),
  ]

  useEffect(() => {
    reloadFetchCategories()
  }, [referenceId])

  useEffect(() => {
    if (error) {
      captureException(error)
    }
  }, [error])

  useEffect(() => {
    migrateLegacyProgress()
  }, [])

  useEffect(() => {
    if (phoneNumberFromToken && referenceId) {
      loadProgress()
    }
  }, [])

  return (
    <BadgesCtx.Provider value={{
      setCurrentCategoryId,
      currentCategory,
      setCurrentTaskId,
      currentTask,
      getCategoryById,
      categories: categoriesWithProgress,
      allBadges: allBadgesWithExtra,
      updateProgress,
      completeExtraBadge,
      completeExtraBadgeTask,
      extraBadgesProgress,
      loadProgress,
      showOnboarding,
      setShowOnboarding
    }}>
      {children}
    </BadgesCtx.Provider>
  )
}
